import { t } from 'i18next';
import isEmpty from 'lodash/isEmpty';

import { getDefaultDatePresets } from 'components/filters/date/keys';
import {
  AmountFilterPassedProps,
  CheckboxFilterPassedProps,
  CurrencyFilterPassedProps,
  DateFilterPassedProps,
  RadioFilterPassedProps,
  RemoteSelectFilterPassedProps,
} from 'components/filters/types';
import { CurrencyItem } from 'components/inputs/select/currencySelect/types';
import { SelectItem } from 'components/inputs/select/select';

import { AccountInfo } from 'types/account';
import { Filter, FilterSection, FilterType } from 'types/filters';
import { RequestPagination, ServerPagination } from 'types/pagination';

import { COLUMN_IDS, LABELS, filterStatusGroups } from './keys';
import { Filters, RequestFilters } from './types';

export const makeFilterSections = (filterMap: Record<string, Filter<unknown>>, showPartnerAccountFilter: boolean): FilterSection[] => {
  return [
    {
      key: 'filters',
      content: [filterMap.statuses, filterMap.locked, filterMap.date],
    },
    {
      key: 'currencyFilters',
      content: [filterMap.fiatCurrencyCode, filterMap.fiatAmount],
    },
    ...(showPartnerAccountFilter
      ? [
          {
            key: 'partnerFilters',
            content: [filterMap.accountUuids],
          },
        ]
      : []),
  ];
};

export const makeFilters = ({
  fiatOptions,
  getMerchants,
}: {
  fiatOptions: CurrencyItem[];
  getMerchants: (params: {
    search?: string;
    pagination?: RequestPagination;
    ids?: string | string[];
  }) => Promise<{ data: SelectItem[]; pagination?: ServerPagination }>;
}): Record<string, Filter<unknown>> => {
  const statusFilter: Filter<CheckboxFilterPassedProps> = {
    key: 'statuses',
    label: t(LABELS.FILTERS.STATUS.LABEL),
    type: FilterType.Checkbox,
    options: [
      {
        key: 'initiated',
        value: filterStatusGroups.initiated,
        label: t(LABELS.FILTERS.STATUS.INITIATED),
      },
      {
        key: 'pending',
        value: filterStatusGroups.pending,
        label: t(LABELS.FILTERS.STATUS.PENDING),
      },
      {
        key: 'submitted',
        value: filterStatusGroups.submitted,
        label: t(LABELS.FILTERS.STATUS.SUBMITTED),
      },
      {
        key: 'confirmed',
        value: filterStatusGroups.confirmed,
        label: t(LABELS.FILTERS.STATUS.CONFIRMED),
      },
      {
        key: 'completed',
        value: filterStatusGroups.completed,
        label: t(LABELS.FILTERS.STATUS.COMPLETED),
      },
      {
        key: 'incomplete',
        value: filterStatusGroups.incomplete,
        label: t(LABELS.FILTERS.STATUS.INCOMPLETE),
      },
      {
        key: 'overpaid',
        value: filterStatusGroups.overpaid,
        label: t(LABELS.FILTERS.STATUS.OVERPAID),
      },
      {
        key: 'expired',
        value: filterStatusGroups.expired,
        label: t(LABELS.FILTERS.STATUS.EXPIRED),
      },
      {
        key: 'canceled',
        value: filterStatusGroups.canceled,
        label: t(LABELS.FILTERS.STATUS.CANCELED),
      },
      {
        key: 'declined',
        value: filterStatusGroups.declined,
        label: t(LABELS.FILTERS.STATUS.DECLINED),
      },
      {
        key: 'failed',
        value: filterStatusGroups.failed,
        label: t(LABELS.FILTERS.STATUS.FAILED),
      },
      {
        key: 'refunded',
        value: filterStatusGroups.refunded,
        label: t(LABELS.FILTERS.STATUS.REFUNDED),
      },
      {
        key: 'partialRefund',
        value: filterStatusGroups.partialRefund,
        label: t(LABELS.FILTERS.STATUS.PARTIAL_REFUND),
      },
    ],
  };

  const lockedFilter: Filter<RadioFilterPassedProps> = {
    key: COLUMN_IDS.LOCKED,
    label: t(LABELS.FILTERS.LOCKED.LABEL),
    type: FilterType.Radio,
    options: [
      {
        key: 'yes',
        value: 'true',
        label: t(LABELS.FILTERS.LOCKED.YES),
      },
      {
        key: 'no',
        value: 'false',
        label: t(LABELS.FILTERS.LOCKED.NO),
      },
    ],
  };

  const datePresets = getDefaultDatePresets();

  const dateFilter: Filter<DateFilterPassedProps> = {
    key: 'date',
    label: t(LABELS.FILTERS.DATE.LABEL),
    type: FilterType.Date,
    presets: datePresets,
  };

  const fiatCurrencyFilter: Filter<CurrencyFilterPassedProps> = {
    key: 'fiatCurrencyCode',
    type: FilterType.CurrencySelect,
    label: t(LABELS.FILTERS.FIAT_CURRENCY.LABEL),
    options: fiatOptions,
    multiple: false,
  };

  const fiatCurrencyAmountFilter: Filter<AmountFilterPassedProps> = {
    key: 'fiatAmount',
    type: FilterType.Amount,
    label: t(LABELS.FILTERS.ORDER_AMOUNT.LABEL),
  };

  const accountFilter: Filter<RemoteSelectFilterPassedProps> = {
    key: 'accountUuids',
    label: t(LABELS.FILTERS.MERCHANT.LABEL),
    type: FilterType.RemoteSelect,
    placeholder: t(LABELS.FILTERS.MERCHANT.PLACEHOLDER),
    multiple: false,
    getItems: getMerchants,
  };

  return [statusFilter, lockedFilter, dateFilter, fiatCurrencyFilter, fiatCurrencyAmountFilter, accountFilter].reduce((acc, cur) => {
    return { ...acc, [cur.key]: cur };
  }, {});
};

export const prepareFilters = ({ locked, ...filters }: Filters, selectedAccount?: AccountInfo) => {
  const output: RequestFilters = {
    ...filters,
  };

  if (filters.accountUuids) {
    const raw = Array.isArray(filters.accountUuids) ? filters.accountUuids : [filters.accountUuids];
    const normalized = raw.filter((id): id is string => Boolean(id));
    if (normalized.length) {
      output.accountUuids = normalized;
    } else {
      delete output.accountUuids;
    }
  } else if (selectedAccount?.uuid) {
    output.accountUuids = [selectedAccount.uuid];
  } else {
    delete output.accountUuids;
  }

  if (locked && !isEmpty(locked)) {
    const [value] = locked;
    output.locked = value ? value === 'true' : false;
  }

  return output;
};
